import type { Store } from '@recipes/contracts';
import { Button, Label, StarRating, Textarea } from '@recipes/ui';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { toastError } from '@/lib/errors';
import { updateStoreLocal } from './catalog-queue';

/** Stars as text for places without React (map labels). */
export const starsText = (rating: number | null | undefined): string =>
  rating ? '★'.repeat(rating) + '☆'.repeat(5 - rating) : '';

/** The household's stars for a store, small and read-only; nothing when it has none. */
export function StoreStars({ rating, className }: { rating: number | null; className?: string }) {
  const { t } = useTranslation();
  if (!rating) return null;
  return (
    <StarRating
      value={rating}
      readOnly
      size="sm"
      label={t('catalog.stores.rating')}
      className={className}
    />
  );
}

/**
 * Rating and comment of a store (user, 2026-09-24): one rating per store, shared by the
 * household like the store itself; the comment is the store's note. Saved through the device
 * copy, so it works offline like the department order.
 */
export function StoreRatingCard({ store }: { store: Store }) {
  const { t } = useTranslation();
  const ids = { heading: useId(), note: useId() };
  const [rating, setRating] = useState<number | null>(store.rating);
  const [note, setNote] = useState(store.note ?? '');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setRating(store.rating);
    setNote(store.note ?? '');
  }, [store.id, store.version, store.rating, store.note]);
  const dirty = rating !== store.rating || note.trim() !== (store.note ?? '');

  const save = async () => {
    setSaving(true);
    try {
      await updateStoreLocal(store, { rating, note: note.trim() || null });
      toast.success(t('catalog.stores.ratingSaved'));
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby={ids.heading}
      className="space-y-3 rounded-lg border p-4"
      data-testid="store-rating"
    >
      <h2 id={ids.heading} className="text-lg font-semibold">
        {t('catalog.stores.rating')}
      </h2>
      <div className="flex flex-wrap items-center gap-3">
        <StarRating
          value={rating}
          allowHalf={false}
          readOnly={!store.editable}
          label={t('catalog.stores.rating')}
          onChange={store.editable ? setRating : undefined}
        />
        {rating === null && (
          <span className="text-sm text-muted-foreground">{t('catalog.stores.noRating')}</span>
        )}
      </div>
      {store.editable ? (
        <div>
          <Label htmlFor={ids.note}>{t('catalog.stores.comment')}</Label>
          <Textarea
            id={ids.note}
            className="mt-1"
            rows={3}
            maxLength={500}
            value={note}
            placeholder={t('catalog.stores.commentPlaceholder')}
            onChange={(e) => setNote(e.target.value)}
            data-testid="store-comment"
          />
        </div>
      ) : (
        note && <p className="whitespace-pre-line text-sm">{note}</p>
      )}
      {store.editable && (
        <Button
          type="button"
          loading={saving}
          disabled={!dirty}
          onClick={() => void save()}
          data-testid="store-rating-save"
        >
          {t('common.save')}
        </Button>
      )}
    </section>
  );
}
